import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Instagram, Send, Loader2, CheckCircle2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import { Toaster, toast } from "sonner";
import { HERO_IMAGES } from "../data/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (form.message.trim().length < 5) e.message = "Tell us a bit more.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data.success) {
        setStatus("success");
        toast.success("Message sent! We'll be in touch soon.");
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      }
    } catch (err) {
      toast.error("Something went wrong. Please call us instead: 0434 508 712");
      setStatus("idle");
    }
  };

  return (
    <div data-testid="page-contact">
      <Toaster position="top-center" theme="dark" richColors />
      <PageHeader
        eyebrow="Say hello"
        title="Get in"
        accent="touch."
        subtitle="Bookings, catering, party orders, sponsorships, or just sending love — we read everything."
        image={HERO_IMAGES[0]}
      />

      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="grid lg:grid-cols-2 gap-10">
          <Reveal>
            <form onSubmit={submit} className="card-dark p-6 md:p-10" data-testid="contact-form">
              <h2 className="text-2xl md:text-3xl font-extrabold">Send us a message</h2>
              <p className="text-white/55 mt-2">We'll reply within 24 hours.</p>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60 font-bold">Name</label>
                  <input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full rounded-xl bg-black/60 border border-white/10 px-4 py-3 text-white focus:border-yellow-brand focus:outline-none focus:ring-1 focus:ring-yellow-brand"
                    placeholder="Your name"
                  />
                  {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60 font-bold">Email</label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-2 w-full rounded-xl bg-black/60 border border-white/10 px-4 py-3 text-white focus:border-yellow-brand focus:outline-none focus:ring-1 focus:ring-yellow-brand"
                    placeholder="you@example.com"
                  />
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60 font-bold">Phone (optional)</label>
                  <input
                    data-testid="contact-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-2 w-full rounded-xl bg-black/60 border border-white/10 px-4 py-3 text-white focus:border-yellow-brand focus:outline-none focus:ring-1 focus:ring-yellow-brand"
                    placeholder="0400 000 000"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-white/60 font-bold">Subject</label>
                  <input
                    data-testid="contact-subject"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="mt-2 w-full rounded-xl bg-black/60 border border-white/10 px-4 py-3 text-white focus:border-yellow-brand focus:outline-none focus:ring-1 focus:ring-yellow-brand"
                    placeholder="Catering, feedback, etc."
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="text-xs uppercase tracking-widest text-white/60 font-bold">Message</label>
                <textarea
                  data-testid="contact-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 w-full rounded-xl bg-black/60 border border-white/10 px-4 py-3 text-white focus:border-yellow-brand focus:outline-none focus:ring-1 focus:ring-yellow-brand resize-none"
                  placeholder="What's the occasion? Any details we should know?"
                />
                {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                data-testid="contact-submit"
                className="btn-primary mt-7 w-full justify-center"
              >
                {status === "submitting" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </>
                ) : status === "success" ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" /> Sent!
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> Send Message
                  </>
                )}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-6">
              <div className="card-dark p-7">
                <h3 className="text-xl font-extrabold">Reach us directly</h3>
                <ul className="mt-5 space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-yellow-brand/15 text-yellow-brand flex items-center justify-center"><Phone className="w-4 h-4" /></div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-white/50 font-bold">Phone</div>
                      <a href="tel:0434508712" className="text-lg font-bold hover:text-yellow-brand">0434 508 712</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-yellow-brand/15 text-yellow-brand flex items-center justify-center"><Mail className="w-4 h-4" /></div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-white/50 font-bold">Email</div>
                      <a href="mailto:info@tasteofegg.com.au" className="text-lg font-bold hover:text-yellow-brand">info@tasteofegg.com.au</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-yellow-brand/15 text-yellow-brand flex items-center justify-center"><MapPin className="w-4 h-4" /></div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-white/50 font-bold">Location</div>
                      <div className="text-lg font-bold">Khau gali</div>
                      <div className="text-white/85">Clayton South, VIC 3169</div>
                      <div className="text-white/55 text-sm">Melbourne, Australia</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-yellow-brand/15 text-yellow-brand flex items-center justify-center"><Instagram className="w-4 h-4" /></div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-white/50 font-bold">Instagram</div>
                      <a href="https://instagram.com/tasteofegg_australia" target="_blank" rel="noreferrer" className="text-lg font-bold hover:text-yellow-brand">@tasteofegg_australia</a>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="card-dark p-7">
                <h3 className="text-xl font-extrabold">Trading hours</h3>
                <ul className="mt-4 space-y-2 text-white/70">
                  <li className="flex justify-between"><span>Mon – Fri</span><span>5:00 PM – 11:00 PM</span></li>
                  <li className="flex justify-between"><span>Sat – Sun</span><span className="text-yellow-brand">5:00 PM – Late</span></li>
                </ul>
              </div>

              <div className="rounded-3xl overflow-hidden border border-white/10 aspect-[4/3] glow-yellow">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3146.8878246618337!2d145.11753237657584!3d-37.933050971945896!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad66bdeb895d3c5%3A0xb054842e6cb07e04!2sTaste%20of%20Egg!5e0!3m2!1sen!2sau!4v1788775309990!5m2!1sen!2sau" className="w-full h-full" style={{ border: 0 }} allowfullscreen="" loading="lazy" referrerpolicy="strict-origin-when-cross-origin"></iframe>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
